/**
 * PropertyPermissionsGuide - Guia explicativo + Gerenciamento de permissões
 * 
 * Unificado com 2 seções:
 * - "Entenda" → Guia explicativo dos papéis
 * - "Gerenciar" → Adicionar/remover pessoas (apenas owner)
 * 
 * Baseado no Tripé:
 * - JUSTIÇA: Todos os papéis claramente explicados
 * - SEGURANÇA: Apenas owner pode modificar, confirmações para ações destrutivas
 * - SIMPLICIDADE: Interface limpa e intuitiva
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { useTranslation } from "../translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Componente de Tooltip de Ajuda (uso inline) - Exportado para uso em outros componentes
export const PermissionsHelpTooltip = ({ children }) => {
  const { t } = useTranslation();
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {children || (
            <button className="ml-1 text-gray-400 hover:text-gray-600">
              <span className="text-sm">ℹ️</span>
            </button>
          )}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-gray-900 text-white p-3">
          <div className="space-y-2 text-xs">
            <div>
              <span className="font-bold text-yellow-400">👑 {t('permGuideOwner')}:</span>
              <span className="ml-1">{t('permGuideOwnerDesc')}</span>
            </div>
            <div>
              <span className="font-bold text-blue-400">👔 {t('permGuideManager')}:</span>
              <span className="ml-1">{t('permGuideManagerDesc')}</span>
            </div>
            <div>
              <span className="font-bold text-green-400">👷 {t('permGuideOperator')}:</span>
              <span className="ml-1">{t('permGuideOperatorDesc')}</span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Modal completo com guia + gerenciamento
const PropertyPermissionsGuide = ({ isOpen, onClose, property, onUpdate }) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('understand'); // 'understand' or 'manage'
  const [activeTab, setActiveTab] = useState('overview');
  
  // Management states
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [showAddManager, setShowAddManager] = useState(false);
  const [showAddOperator, setShowAddOperator] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [newCredentials, setNewCredentials] = useState(null);
  
  // Form states
  const [managerEmail, setManagerEmail] = useState("");
  const [managerName, setManagerName] = useState("");
  const [operatorEmail, setOperatorEmail] = useState("");

  const tabs = [
    { id: 'overview', label: `📋 ${t('permGuideOverview')}` },
    { id: 'roles', label: `👥 ${t('permGuideRoles')}` },
    { id: 'transfer', label: `🔄 ${t('permGuideTransfer')}` },
    { id: 'plans', label: `💳 ${t('permGuidePlans')}` },
  ];

  // Fetch permissions when switching to manage tab
  const fetchPermissions = useCallback(async () => {
    if (!property?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/property/${property.id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissions(response.data);
    } catch (err) {
      console.error("Error fetching permissions:", err);
      setError(err.response?.data?.detail || "Erro ao carregar permissões");
    } finally {
      setLoading(false);
    }
  }, [property?.id]);

  useEffect(() => {
    if (activeSection === 'manage' && property?.id) {
      fetchPermissions();
    }
  }, [activeSection, property?.id, fetchPermissions]);

  const handleUpdatePermissions = async (updateData) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API}/property/${property.id}/permissions`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check for new account creation
      const messages = response.data.messages || [];
      const passwordMsg = messages.find(m => m.includes("senha:"));
      if (passwordMsg) {
        const match = passwordMsg.match(/\(senha: ([^)]+)\)/);
        if (match) {
          setNewCredentials({
            email: updateData.manager_email || updateData.add_operator_email,
            password: match[1]
          });
        }
      }
      
      await fetchPermissions();
      if (onUpdate) onUpdate();
      
      return true;
    } catch (err) {
      console.error("Error updating permissions:", err);
      alert(err.response?.data?.detail || "Erro ao atualizar permissões");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleAddManager = async () => {
    if (!managerEmail.trim() || !managerEmail.includes("@")) {
      alert(t('permMgmtErrorInvalidEmail'));
      return;
    }
    
    const success = await handleUpdatePermissions({
      manager_email: managerEmail.trim(),
      manager_name: managerName.trim() || null
    });
    
    if (success) {
      setShowAddManager(false);
      setManagerEmail("");
      setManagerName("");
    }
  };

  const handleAddOperator = async () => {
    if (!operatorEmail.trim() || !operatorEmail.includes("@")) {
      alert(t('permMgmtErrorInvalidEmail'));
      return;
    }
    
    const success = await handleUpdatePermissions({
      add_operator_email: operatorEmail.trim()
    });
    
    if (success) {
      setShowAddOperator(false);
      setOperatorEmail("");
    }
  };

  const handleRemoveManager = async () => {
    await handleUpdatePermissions({ clear_manager: true });
    setConfirmRemove(null);
  };

  const handleRemoveOperator = async (email) => {
    await handleUpdatePermissions({ remove_operator_email: email });
    setConfirmRemove(null);
  };

  const handlePromoteToManager = async (opEmail) => {
    await handleUpdatePermissions({ remove_operator_email: opEmail });
    await handleUpdatePermissions({ manager_email: opEmail });
  };

  const isOwner = permissions?.your_role === "owner";

  // Render the guide content based on active tab
  const renderGuideContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">{t('permGuideHowWorks')}</h4>
              <p className="text-blue-700 text-sm">
                {t('permGuideHowWorksDesc')}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h5 className="font-medium text-gray-800 mb-3">{t('permGuidePracticalExample')}</h5>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded">
                  <span>👑</span>
                  <span><strong>Maria</strong> {t('permGuideOwnerExample')}</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                  <span>👔</span>
                  <span><strong>Fernando</strong> {t('permGuideManagerExample')}</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
                  <span>👷</span>
                  <span><strong>João</strong> {t('permGuideOperatorExample')}</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h5 className="font-medium text-amber-800 mb-2">⚠️ {t('permGuideImportantPlans')}</h5>
              <p className="text-amber-700 text-sm">
                {t('permGuideImportantPlansDesc')}
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-purple-700 text-sm">
                <strong>💡 {t('permGuideSummary')}</strong> {t('permGuideSummaryDesc')}
              </p>
            </div>
          </div>
        );

      case 'roles':
        return (
          <div className="space-y-4">
            {/* Proprietário */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-yellow-100 px-4 py-2 border-b">
                <h4 className="font-semibold text-yellow-800 flex items-center gap-2">
                  <span>👑</span> {t('permGuideOwner')} (owner)
                </h4>
              </div>
              <div className="p-4 space-y-2">
                <p className="text-gray-700 text-sm">{t('permGuideOwnerDesc')}</p>
                <div className="text-sm">
                  <strong className="text-green-600">✓ {t('permGuideOwnerCan')}</strong>
                  <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                    {t('permGuideOwnerCanList').split('|').map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Responsável */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-blue-100 px-4 py-2 border-b">
                <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                  <span>👔</span> {t('permGuideManager')} (manager)
                </h4>
              </div>
              <div className="p-4 space-y-2">
                <p className="text-gray-700 text-sm">{t('permGuideManagerDesc')}</p>
                <div className="text-sm">
                  <strong className="text-green-600">✓ {t('permGuideManagerCan')}</strong>
                  <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                    {t('permGuideManagerCanList').split('|').map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
                <div className="text-sm mt-2">
                  <strong className="text-red-600">✗ {t('permGuideManagerCannot')}</strong>
                  <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                    {t('permGuideManagerCannotList').split('|').map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Operador */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-green-100 px-4 py-2 border-b">
                <h4 className="font-semibold text-green-800 flex items-center gap-2">
                  <span>👷</span> {t('permGuideOperator')} (operator)
                </h4>
              </div>
              <div className="p-4 space-y-2">
                <p className="text-gray-700 text-sm">{t('permGuideOperatorDesc')}</p>
                <div className="text-sm">
                  <strong className="text-green-600">✓ {t('permGuideOperatorCan')}</strong>
                  <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                    {t('permGuideOperatorCanList').split('|').map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
                <div className="text-sm mt-2">
                  <strong className="text-red-600">✗ {t('permGuideOperatorCannot')}</strong>
                  <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                    {t('permGuideOperatorCannotList').split('|').map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'transfer':
        return (
          <div className="space-y-4">
            {/* O que é */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 mb-2">🔄 {t('permGuideTransferTitle')}</h4>
              <p className="text-purple-700 text-sm">
                {t('permGuideTransferDesc')}
              </p>
            </div>

            {/* O que MUDA */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-orange-100 px-4 py-2 border-b">
                <h4 className="font-semibold text-orange-800">{t('permGuideTransferWhatChanges')}</h4>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                  <span className="text-lg shrink-0">👑</span>
                  <p className="text-sm text-gray-700">{t('permGuideTransferChangeOwner')}</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                  <span className="text-lg shrink-0">💳</span>
                  <p className="text-sm text-red-800 font-medium">{t('permGuideTransferChangePlan')}</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                  <span className="text-lg shrink-0">🔑</span>
                  <p className="text-sm text-gray-700">{t('permGuideTransferChangeAuth')}</p>
                </div>
              </div>
            </div>

            {/* O que NÃO muda */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-green-100 px-4 py-2 border-b">
                <h4 className="font-semibold text-green-800">{t('permGuideTransferWhatStays')}</h4>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-green-600 shrink-0">✅</span>
                  <p>{t('permGuideTransferStayData')}</p>
                </div>
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-green-600 shrink-0">✅</span>
                  <p>{t('permGuideTransferStayTeam')}</p>
                </div>
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-green-600 shrink-0">✅</span>
                  <p>{t('permGuideTransferStayConfig')}</p>
                </div>
              </div>
            </div>

            {/* Alerta sobre Plano */}
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
              <h5 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <span>⚠️</span> {t('permGuideTransferPlanWarningTitle')}
              </h5>
              <p className="text-amber-800 text-sm">
                {t('permGuideTransferPlanWarning')}
              </p>
            </div>

            {/* Como funciona */}
            <div className="space-y-3">
              <h5 className="font-medium text-gray-800">{t('permGuideTransferSteps')}</h5>
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold shrink-0">{step}</div>
                  <p className="text-sm text-gray-700 pt-1">{t(`permGuideTransferStep${step}`)}</p>
                </div>
              ))}
            </div>

            {/* Quem pode */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h5 className="font-medium text-blue-800 mb-1">{t('permGuideTransferWhoCanTitle')}</h5>
              <p className="text-blue-700 text-sm">{t('permGuideTransferWhoCan')}</p>
            </div>

            {/* Resumo final */}
            <div className="bg-gray-100 border rounded-lg p-3">
              <p className="text-gray-700 text-sm font-medium">
                💡 {t('permGuideTransferNote')}
              </p>
            </div>
          </div>
        );

      case 'plans':
        return (
          <div className="space-y-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h4 className="font-semibold text-indigo-800 mb-2">💳 {t('permGuidePlansTitle')}</h4>
              <p className="text-indigo-700 text-sm">
                {t('permGuideImportantPlansDesc')}
              </p>
            </div>

            {/* Plans list - Updated to match actual plans */}
            {[
              { key: 'Free', icon: '🆓', bgColor: 'bg-gray-100', textColor: 'text-gray-800' },
              { key: 'Professional', icon: '💵', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
              { key: 'Enterprise', icon: '💼', bgColor: 'bg-green-100', textColor: 'text-green-800' },
              { key: 'Complete', icon: '🏆', bgColor: 'bg-purple-100', textColor: 'text-purple-800' },
            ].map(({ key, icon, bgColor, textColor }) => (
              <div key={key} className="border rounded-lg overflow-hidden">
                <div className={`${bgColor} px-4 py-2 border-b`}>
                  <h4 className={`font-semibold ${textColor}`}>{icon} {t(`permGuide${key}`)}</h4>
                </div>
                <div className="p-4">
                  <ul className="text-sm text-gray-600 space-y-1">
                    {t(`permGuide${key}Desc`).split('|').map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  // Render the management content
  const renderManageContent = () => {
    if (!property) {
      return (
        <div className="p-8 text-center">
          <p className="text-amber-600 mb-2">⚠️</p>
          <p className="text-gray-600">{t('selectProperty') || 'Selecione uma propriedade primeiro'}</p>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('permMgmtLoading')}</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-8 text-center">
          <p className="text-red-600 mb-4">{t('permMgmtError')}</p>
          <Button onClick={fetchPermissions} variant="outline">
            {t('permMgmtRetry')}
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Owner Section */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">👑</span>
            <h3 className="font-semibold text-yellow-800">{t('permMgmtOwner')}</h3>
          </div>
          <p className="text-xs text-yellow-700 mb-3">{t('permMgmtOwnerDesc')}</p>
          
          {permissions?.owner && (
            <div className="bg-white rounded p-3">
              <p className="font-medium text-gray-800">
                {permissions.owner.name || permissions.owner.email}
                {permissions.your_role === "owner" && (
                  <span className="ml-2 text-xs text-green-600 font-normal">{t('permMgmtOwnerYou')}</span>
                )}
              </p>
              <p className="text-sm text-gray-500">{permissions.owner.email}</p>
            </div>
          )}
        </div>

        {/* Manager Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xl">👔</span>
              <h3 className="font-semibold text-blue-800">{t('permMgmtManager')}</h3>
            </div>
            {isOwner && !permissions?.manager && (
              <Button 
                size="sm" 
                onClick={() => setShowAddManager(true)}
                className="bg-blue-600 hover:bg-blue-700 text-xs"
              >
                + {t('permMgmtAddManager')}
              </Button>
            )}
          </div>
          <p className="text-xs text-blue-700 mb-3">{t('permMgmtManagerDesc')}</p>
          
          {permissions?.manager ? (
            <div className="bg-white rounded p-3 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-800">{permissions.manager.name || permissions.manager.email}</p>
                <p className="text-sm text-gray-500">{permissions.manager.email}</p>
              </div>
              {isOwner && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setConfirmRemove({ type: 'manager', email: permissions.manager.email, name: permissions.manager.name })}
                  className="text-red-600 border-red-300 hover:bg-red-50 text-xs"
                >
                  {t('permMgmtRemove')}
                </Button>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-sm italic">{t('permMgmtNoManager')}</p>
          )}
        </div>

        {/* Operators Section */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xl">👷</span>
              <h3 className="font-semibold text-green-800">{t('permMgmtOperators')}</h3>
            </div>
            {isOwner && (
              <Button 
                size="sm" 
                onClick={() => setShowAddOperator(true)}
                className="bg-green-600 hover:bg-green-700 text-xs"
              >
                + {t('permMgmtAddOperator')}
              </Button>
            )}
          </div>
          <p className="text-xs text-green-700 mb-3">{t('permMgmtOperatorsDesc')}</p>
          
          {permissions?.operators && permissions.operators.length > 0 ? (
            <div className="space-y-2">
              {permissions.operators.map((op) => (
                <div key={op.id} className="bg-white rounded p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">{op.name || op.email}</p>
                    <p className="text-sm text-gray-500">{op.email}</p>
                  </div>
                  {isOwner && (
                    <div className="flex gap-2">
                      {!permissions.manager && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handlePromoteToManager(op.email)}
                          className="text-blue-600 border-blue-300 hover:bg-blue-50 text-xs"
                          disabled={saving}
                        >
                          ⬆️
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setConfirmRemove({ type: 'operator', email: op.email, name: op.name })}
                        className="text-red-600 border-red-300 hover:bg-red-50 text-xs"
                      >
                        {t('permMgmtRemove')}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm italic">{t('permMgmtNoOperators')}</p>
          )}
        </div>

        {/* Read-only notice for non-owners */}
        {!isOwner && permissions && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800 flex items-center gap-2">
              <span>🔒</span>
              {t('permMgmtOnlyOwnerCanEdit')}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden p-0">
          <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-indigo-600 to-purple-600">
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <span>🔐</span>
              {t('permGuideTitle')}
              {property && <span className="text-indigo-200 font-normal text-base ml-2">- {property.name}</span>}
            </DialogTitle>
          </DialogHeader>

          {/* Main Section Tabs (Understand / Manage) */}
          <div className="border-b px-4 py-2 bg-gray-50 flex gap-2">
            <button
              onClick={() => setActiveSection('understand')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeSection === 'understand'
                  ? 'bg-indigo-100 text-indigo-800'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              📖 {t('permGuideOverview')}
            </button>
            <button
              onClick={() => setActiveSection('manage')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeSection === 'manage'
                  ? 'bg-amber-100 text-amber-800'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              👥 {t('permMgmtTitle')}
            </button>
          </div>

          {/* Content Area */}
          {activeSection === 'understand' ? (
            <>
              {/* Sub-tabs for Guide */}
              <div className="border-b px-4 py-2 flex gap-1 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'bg-indigo-100 text-indigo-800 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="p-6 overflow-y-auto max-h-[50vh]">
                {renderGuideContent()}
              </div>
            </>
          ) : (
            <div className="p-6 overflow-y-auto max-h-[55vh]">
              {renderManageContent()}
            </div>
          )}

          {/* Footer */}
          <div className="border-t px-6 py-3 bg-gray-50 flex justify-end">
            <Button onClick={onClose} variant="outline" size="sm">
              {t('close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Manager Dialog */}
      <Dialog open={showAddManager} onOpenChange={setShowAddManager}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>👔</span> {t('permMgmtAddManager')}
            </DialogTitle>
            <DialogDescription>
              {t('permMgmtManagerDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="manager-email">Email *</Label>
              <Input
                id="manager-email"
                type="email"
                placeholder={t('permMgmtEmailPlaceholder')}
                value={managerEmail}
                onChange={(e) => setManagerEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="manager-name">{t('permMgmtNamePlaceholder')}</Label>
              <Input
                id="manager-name"
                type="text"
                placeholder={t('permMgmtNamePlaceholder')}
                value={managerName}
                onChange={(e) => setManagerName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddManager(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleAddManager} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? "..." : t('confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Operator Dialog */}
      <Dialog open={showAddOperator} onOpenChange={setShowAddOperator}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>👷</span> {t('permMgmtAddOperator')}
            </DialogTitle>
            <DialogDescription>
              {t('permMgmtOperatorsDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="operator-email">Email *</Label>
              <Input
                id="operator-email"
                type="email"
                placeholder={t('permMgmtEmailPlaceholder')}
                value={operatorEmail}
                onChange={(e) => setOperatorEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddOperator(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleAddOperator} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving ? "..." : t('confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Remove Dialog */}
      <AlertDialog open={!!confirmRemove} onOpenChange={() => setConfirmRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <span>⚠️</span> {t('permMgmtConfirmRemove')} {confirmRemove?.name || confirmRemove?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('permMgmtConfirmRemoveDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmRemove?.type === 'manager') {
                  handleRemoveManager();
                } else {
                  handleRemoveOperator(confirmRemove?.email);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('permMgmtRemove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Credentials Dialog */}
      <Dialog open={!!newCredentials} onOpenChange={() => setNewCredentials(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <span>✅</span> {t('permMgmtNewAccountCreated')}
            </DialogTitle>
            <DialogDescription>
              {t('permMgmtShareCredentials')}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
            <div className="space-y-2">
              <p className="text-sm">
                <strong>Email:</strong> {newCredentials?.email}
              </p>
              <p className="text-sm">
                <strong>{t('permMgmtTempPassword')}:</strong>{" "}
                <code className="bg-amber-100 px-2 py-1 rounded font-mono">
                  {newCredentials?.password}
                </code>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setNewCredentials(null)} className="bg-green-600 hover:bg-green-700">
              {t('understood')} ✓
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PropertyPermissionsGuide;
